import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  doc, 
  getDoc, 
  getDocs, 
  collection, 
  updateDoc, 
  addDoc,
  query,
  where,
  arrayUnion,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase/config';
import PageWrapper from '../layout/PageWrapper';
import { extractIFCEData, validateExtractedData } from '../utils/pdfExtractor';
import SurveillanceStatus from '../components/SurveillanceStatus';
import SurveillanceHistoryModal from '../components/SurveillanceHistoryModal';
import MedicalTicketModal from '../components/MedicalTicketModal';

import { 
  ArrowLeft, 
  Edit, 
  Save,
  X,
  MapPin, 
  Calendar, 
  User, 
  Hash, 
  Heart, 
  FileText, 
  AlertTriangle,
  Phone,
  Mail,
  Building,
  Upload,
  FileCheck,
  Loader2,
  Move,
  ExternalLink,
  Plus,
  Eye,
  ChevronDown,
  ChevronUp,
  Check,
  Clock as ClockIcon,
  Syringe,
  Trash2
} from 'lucide-react';
import { GiHorseHead } from 'react-icons/gi';
import { FaHandHoldingMedical } from "react-icons/fa6";
import { PiFarm } from 'react-icons/pi';
import { BsPersonFill } from 'react-icons/bs';
import { MdWhereToVote } from "react-icons/md";

// Couleurs par défaut si Firebase n'est pas disponible
const DEFAULT_TAG_COLORS = [
  { id: 1, nom: 'Bleu', codeHex: '#2563eb' },
  { id: 2, nom: 'Rouge', codeHex: '#ef4444' },
  { id: 3, nom: 'Vert', codeHex: '#22c55e' },
  { id: 4, nom: 'Jaune', codeHex: '#eab308' },
  { id: 5, nom: 'Orange', codeHex: '#f97316' },
  { id: 6, nom: 'Violet', codeHex: '#8b5cf6' },
  { id: 7, nom: 'Rose', codeHex: '#ec4899' },
  { id: 8, nom: 'Turquoise', codeHex: '#06b6d4' },
  { id: 9, nom: 'Gris', codeHex: '#6b7280' },
  { id: 10, nom: 'Noir', codeHex: '#111827' },
  { id: 11, nom: 'Marron', codeHex: '#92400e' },
  { id: 12, nom: 'Blanc', codeHex: '#f3f4f6' }
];

const surveillanceColors = { 
  vert: "#22c55e", 
  jaune: "#facc15", 
  orange: "#fb923c", 
  rouge: "#ef4444" 
};

const categories = ["Poney", "Cheval", "Âne"];
const races = [
  "Pur-Sang Anglais",
  "Pur-Sang Arabe", 
  "Lipizzan",
  "Akhal-Teke",
  "Frison",
  "Islandais",
  "Quarter Horse",
  "Paint Horse", 
  "Cleveland Bay",
  "Selle Français",
  "KWPN",
  "Holsteiner",
  "Oldenbourg", 
  "Hanovrien",
  "Trakehner",
  "Irish Sport Horse",
  "Zangersheide",
  "BWP",
  "Belgian Warmblood",
  "Anglo-Arabe",
  "Connemara",
  "New Forest",
  "Irish Cob",
  "Camargue",
  "Lusitanien",
  "PRE",
  "Fjord",
  "Comtois",
  "Percheron",
  "Auxois",
  "Breton",
  "Cob Normand",
  "Trait du Nord",
  "Henson",
  "Poney Français de Selle",
  "Ardennais",
  "Origine Non Constatée",
  "Autre"
];


const UnifiedHorsePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'nouveau';
  
  const [isEditing, setIsEditing] = useState(isNew);
  const [loading, setLoading] = useState(!isNew);
  const [pdfProcessing, setPdfProcessing] = useState(false);
  
  // États des données
  const [horse, setHorse] = useState(null);
  const [proprietaire, setProprietaire] = useState(null);
  const [zone, setZone] = useState(null);
  const [tagColors, setTagColors] = useState([]);
  const [zones, setZones] = useState([]);
  const [proprietaires, setProprietaires] = useState([]);
  const [couleurs, setCouleurs] = useState([]);
  
  // États pour les tickets
  const [medicalTickets, setMedicalTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  
  // États pour les actes
  const [actes, setActes] = useState([]);
  const [loadingActes, setLoadingActes] = useState(false);
  const [showActes, setShowActes] = useState(false);
  const [intervenants, setIntervenants] = useState([]);
  
  // État du formulaire avec structure ifceLinks
  const [formData, setFormData] = useState({
    type: "equide",
    nom: "",
    proprietaire: "",
    sire: "",
    dateNaissance: "",
    sexe: "",
    categorie: "",
    tag: "",
    transpondeur: "",
    zoneId: "",
    localisation: "",
    dateDeces: "",
    NotesGenerales: "",
    NotesSantee: "",
    surveillanceEtat: "vert", // État de surveillance par défaut
    surveillanceMessage: "", // Message de surveillance
    surveillanceHistorique: [], // Historique de surveillance
    couleur: "",
    race: "",
    pouliniere: false,
    pere: "",
    mere: "",
    pereDeMere: "",
    naisseur: "",
    // Structure pour les liens IFCE stables
    ifceLinks: {
      main: null,
      pere: null,
      mere: null,
      pereDeMere: null,
      all: {}
    },
    // Compatibilité avec l'ancienne structure
    links: {}
  });

  // États pour gérer les modals
  const [showMedicalModal, setShowMedicalModal] = useState(false);
  const [showSurveillanceHistory, setShowSurveillanceHistory] = useState(false);

  // Fonction pour obtenir le label de l'état de surveillance
  const getSurveillanceLabel = (state) => {
    const labels = {
      vert: "Normal",
      jaune: "Attention",
      orange: "Vigilance",
      rouge: "Critique"
    };
    return labels[state] || state;
  };

  // Charger les tickets médicaux uniquement
  const loadTickets = async () => {
    if (!horse || !horse.id) return;
    
    setLoadingTickets(true);
    try {
      const q = query(
        collection(db, 'tickets'),
        where('horseId', '==', horse.id),
        where('statut', '==', 'ouvert'),
        where('type', '==', 'medical') // Seulement les tickets médicaux
      );
      
      const snapshot = await getDocs(q);
      const medical = [];
      
      snapshot.forEach((doc) => {
        const data = { id: doc.id, ...doc.data() };
        if (data.type === 'medical') {
          medical.push(data);
        }
      });
      
      setMedicalTickets(medical);
    } catch (error) {
      console.error('Erreur chargement tickets:', error);
    } finally {
      setLoadingTickets(false);
    }
  };

  // Charger les intervenants
  const loadIntervenants = async () => {
    try {
      const intervenantsSnap = await getDocs(collection(db, 'intervenants'));
      const intervenantsData = intervenantsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setIntervenants(intervenantsData);
    } catch (error) {
      console.error('Erreur chargement intervenants:', error);
    }
  };

  // Charger les actes de l'animal
  const loadActes = async () => {
    if (!horse || !horse.id) return;
    
    setLoadingActes(true);
    try {
      // Essayer d'abord avec orderBy
      try {
        const q = query(
          collection(db, 'actes'),
          where('animalId', '==', horse.id),
          where('statut', '!=', 'supprime'),
          orderBy('statut'),
          orderBy('dateActe', 'desc')
        );
        
        const snapshot = await getDocs(q);
        const actesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setActes(actesData);
      } catch (indexError) {
        // Si l'index n'existe pas, faire le tri côté client
        console.log('Index composite non trouvé, tri côté client');
        const q = query(
          collection(db, 'actes'),
          where('animalId', '==', horse.id)
        );
        
        const snapshot = await getDocs(q);
        const actesData = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(acte => acte.statut !== 'supprime')
          .sort((a, b) => {
            const dateA = new Date(a.dateActe + ' ' + (a.heureActe || '00:00'));
            const dateB = new Date(b.dateActe + ' ' + (b.heureActe || '00:00'));
            return dateB - dateA;
          });
        
        setActes(actesData);
      }
    } catch (error) {
      console.error('Erreur chargement actes:', error);
      setActes([]);
    } finally {
      setLoadingActes(false);
    }
  };

  // Fonction pour obtenir la couleur du statut
  const getStatutColor = (statut) => {
    switch (statut) {
      case 'realise': return 'bg-green-100 text-green-800 border-green-200';
      case 'planifie': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'annule': return 'bg-red-100 text-red-800 border-red-200';
      case 'reporte': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatutLabel = (statut) => {
    switch (statut) {
      case 'realise': return 'Réalisé';
      case 'planifie': return 'Planifié';
      case 'annule': return 'Annulé';
      case 'reporte': return 'Reporté';
      default: return statut;
    }
  };

  const getStatutIcon = (statut) => {
    switch (statut) {
      case 'realise': return <Check className="h-3 w-3" />;
      case 'planifie': return <ClockIcon className="h-3 w-3" />;
      case 'annule': return <X className="h-3 w-3" />;
      case 'reporte': return <ClockIcon className="h-3 w-3" />;
      default: return null;
    }
  };

  // Navigation vers un acte
  const handleActeClick = (acteId) => {
    navigate(`/actes?acteId=${acteId}`);
  };

  // Navigation vers nouvel acte
  const handleNewActe = () => {
    if (horse && horse.id) {
      navigate(`/actes?animalId=${horse.id}`);
    }
  };

  // Gestion du changement d'état de surveillance
  const handleSurveillanceChange = async (newState, message) => {
    if (!horse || !horse.id) return;
    
    const now = new Date().toISOString();
    const historyEntry = {
      date: now,
      acteur: 'Utilisateur',
      etat: newState,
      message: message || '',
      ancienEtat: formData.surveillanceEtat || 'vert',
      ancienMessage: formData.surveillanceMessage || '',
      action: 'modification'
    };

    const updatedData = {
      surveillanceEtat: newState,
      surveillanceMessage: message || '',
      surveillanceHistorique: arrayUnion(historyEntry)
    };

    try {
      await updateDoc(doc(db, 'animaux', horse.id), updatedData);
      
      // Mettre à jour l'état local
      setFormData(prev => ({
        ...prev,
        surveillanceEtat: newState,
        surveillanceMessage: message || '',
        surveillanceHistorique: [...(prev.surveillanceHistorique || []), historyEntry]
      }));
      
      setHorse(prev => ({
        ...prev,
        surveillanceEtat: newState,
        surveillanceMessage: message || '',
        surveillanceHistorique: [...(prev.surveillanceHistorique || []), historyEntry]
      }));
      
    } catch (error) {
      console.error('Erreur mise à jour surveillance:', error);
      alert('Erreur lors de la mise à jour de l\'état de surveillance');
    }
  };

  // Gestion du glisser-déposer PDF
  useEffect(() => {
    let mounted = true;
    
    const initializePage = async () => {
      try {
        console.log('=== DÉBUT INITIALISATION ===');
        console.log('isNew:', isNew, 'id:', id);
        
        await loadBasicData();
        
        if (!isNew && id && id !== 'nouveau') {
          await loadHorseDetails();
        }
        console.log('=== FIN INITIALISATION ===');
      } catch (error) {
        console.error('Erreur initialisation:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    
    initializePage();
    
    return () => {
      mounted = false;
    };
  }, [id]);

  // Charger les tickets quand le cheval est chargé
  useEffect(() => {
    if (horse && horse.id) {
      loadTickets();
      loadActes();
      loadIntervenants();
    }
  }, [horse]);

  const loadBasicData = async () => {
    try {
      console.log('Chargement des données de base...');
      
      const [zonesSnap, tagColorsSnap, proprietairesSnap] = await Promise.all([
        getDocs(collection(db, "lieux")),
        getDocs(collection(db, "tagCouleursAnimaux")),
        getDocs(collection(db, "proprietaires"))
      ]);
      
      console.log('Zones:', zonesSnap.docs.length);
      console.log('Propriétaires:', proprietairesSnap.docs.length);
      
      setZones(zonesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setProprietaires(proprietairesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      
      // Traitement des couleurs de tags
      let colorsData = [];
      tagColorsSnap.forEach(doc => {
        const data = doc.data();
        if (data.id && data.codeHex) {
          colorsData.push({ 
            id: data.id,
            nom: data.nom || '',
            codeHex: data.codeHex,
            ordre: data.ordre || data.id
          });
        }
      });
      colorsData.sort((a, b) => a.ordre - b.ordre);
      setTagColors(colorsData.length > 0 ? colorsData : DEFAULT_TAG_COLORS);
      
      // Charger les couleurs de chevaux
      try {
        const response = await fetch('/horsecouleurs.txt');
        const text = await response.text();
        const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
        setCouleurs(lines);
      } catch {
        setCouleurs(['Alezan', 'Bai', 'Noir', 'Gris', 'Palomino', 'Pie', 'Rouan']);
      }
      
      console.log('Données de base chargées avec succès');
      
    } catch (error) {
      console.error('Erreur chargement données de base:', error);
      setTagColors(DEFAULT_TAG_COLORS);
      setCouleurs(['Alezan', 'Bai', 'Noir', 'Gris', 'Palomino', 'Pie', 'Rouan']);
    }
  };

  const loadHorseDetails = async () => {
    try {
      setLoading(true);
      console.log('Chargement du cheval ID:', id);
      
      const animalDoc = await getDoc(doc(db, 'animaux', id));
      if (!animalDoc.exists()) {
        console.error('Animal non trouvé');
        navigate('/animaux');
        return;
      }
      
      const animalData = { id: animalDoc.id, ...animalDoc.data() };
      console.log('Données animal:', animalData);
      
      // Migration de l'ancien champ vers le nouveau si nécessaire
      if (animalData.SurveillanceEtat && !animalData.surveillanceEtat) {
        console.log('Migration: SurveillanceEtat -> surveillanceEtat');
        animalData.surveillanceEtat = animalData.SurveillanceEtat;
        delete animalData.SurveillanceEtat;
      }

      if (!animalData.surveillanceEtat) {
        animalData.surveillanceEtat = 'vert';
      }

      if (!animalData.surveillanceMessage) {
        animalData.surveillanceMessage = '';
      }
      if (!animalData.surveillanceHistorique) {
        animalData.surveillanceHistorique = [];
      }
      
      setHorse(animalData);
      setFormData(animalData);

      // Charger le propriétaire
      if (animalData.proprietaire) {
        try {
          const proprietaireDoc = await getDoc(doc(db, 'proprietaires', animalData.proprietaire));
          if (proprietaireDoc.exists()) {
            setProprietaire({ id: proprietaireDoc.id, ...proprietaireDoc.data() });
          }
        } catch (error) {
          console.error('Erreur chargement propriétaire:', error);
        }
      }

      // Charger la zone
      if (animalData.zoneId) {
        try {
          const zoneDoc = await getDoc(doc(db, 'lieux', animalData.zoneId));
          if (zoneDoc.exists()) {
            setZone({ id: zoneDoc.id, ...zoneDoc.data() });
          }
        } catch (error) {
          console.error('Erreur chargement zone:', error);
        }
      }

    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fonctions utilitaires pour les liens IFCE
  const getIFCELink = (type = 'main') => {
    // Nouvelle structure avec ifceLinks
    if (displayData.ifceLinks && displayData.ifceLinks[type]) {
      return displayData.ifceLinks[type];
    }
    
    // Compatibilité avec l'ancienne structure
    if (type === 'main') {
      // Ancienne structure : chercher avec le nom
      if (displayData.links && displayData.nom && displayData.links[displayData.nom]) {
        return displayData.links[displayData.nom];
      }
    } else {
      // Pour les parents, chercher par nom
      const parentName = displayData[type]; // type = 'pere', 'mere', 'pereDeMere'
      if (parentName && displayData.links) {
        // Recherche exacte
        if (displayData.links[parentName]) {
          return displayData.links[parentName];
        }
        // Recherche approximative
        const matchingKey = Object.keys(displayData.links).find(key => 
          key.toLowerCase().startsWith(parentName.toLowerCase())
        );
        if (matchingKey) {
          return displayData.links[matchingKey];
        }
      }
    }
    
    return null;
  };

  const getIFCESearchUrl = (horseName, sire = null) => {
    if (sire) {
      return `https://www.ifce.fr/chevaux/recherche?sire=${sire}`;
    }
    if (horseName) {
      return `https://www.ifce.fr/chevaux/recherche?nom=${encodeURIComponent(horseName)}`;
    }
    return 'https://www.ifce.fr/chevaux/recherche';
  };

  // Calcul de l'âge
  const getAge = (dateNaissance) => {
    if (!dateNaissance) return null;
    const today = new Date();
    const birthDate = new Date(dateNaissance);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const getTagColor = (tagId) => {
    if (!tagId) return null;
    const numericId = parseInt(tagId);
    return tagColors.find(tc => tc.id === numericId);
  };

  const getProprietaireTagColor = () => {
    if (!proprietaire) return null;
    
    if (proprietaire.tagCouleur) {
      const numericId = parseInt(proprietaire.tagCouleur);
      if (!isNaN(numericId)) {
        const found = tagColors.find(tc => tc.id === numericId);
        if (found) return found.codeHex;
      }
    }
    
    if (proprietaire.tagCouleurNom) {
      const found = tagColors.find(tc => tc.nom === proprietaire.tagCouleurNom);
      if (found) return found.codeHex;
    }
    
    return null;
  };

  // Gestion des modes
  const handleEdit = () => {
    setFormData(horse || {
      type: "equide",
      nom: "",
      proprietaire: "",
      sire: "",
      dateNaissance: "",
      sexe: "",
      categorie: "",
      tag: "",
      transpondeur: "",
      zoneId: "",
      localisation: "",
      dateDeces: "",
      NotesGenerales: "",
      NotesSantee: "",
      surveillanceEtat: "vert",
      surveillanceMessage: "",
      surveillanceHistorique: [],
      couleur: "",
      race: "",
      pouliniere: false,
      pere: "",
      mere: "",
      pereDeMere: "",
      naisseur: "",
      ifceLinks: {
        main: null,
        pere: null,
        mere: null,
        pereDeMere: null,
        all: {}
      },
      links: {}
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (isNew) {
      navigate('/animaux');
    } else {
      setFormData(horse);
      setIsEditing(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // S'assurer que les champs de surveillance existent
      const dataToSave = {
        ...formData,
        surveillanceEtat: formData.surveillanceEtat || 'vert',
        surveillanceMessage: formData.surveillanceMessage || '',
        surveillanceHistorique: formData.surveillanceHistorique || []
      };
      
      if (isNew) {
        // Pour un nouvel animal, ajouter une entrée d'historique de création
        const now = new Date().toISOString();
        dataToSave.surveillanceHistorique = [{
          date: now,
          acteur: 'Utilisateur',
          etat: dataToSave.surveillanceEtat,
          message: dataToSave.surveillanceMessage || '',
          action: 'creation'
        }];
        
        const docRef = await addDoc(collection(db, "animaux"), dataToSave);
        navigate(`/animaux/${docRef.id}`);
      } else {
        await updateDoc(doc(db, "animaux", id), dataToSave);
        setHorse(dataToSave);
        await loadHorseDetails();
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Gestion spéciale pour les liens IFCE imbriqués
    if (name.startsWith('ifceLinks.')) {
      const linkType = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        ifceLinks: {
          ...prev.ifceLinks,
          [linkType]: value
        }
      }));
    } else {
      setFormData(prev => ({ 
        ...prev, 
        [name]: type === 'checkbox' ? checked : value 
      }));
    }
  };

  const handleTagSelect = (tagId) => {
    setFormData(prev => ({ ...prev, tag: tagId.toString() }));
  };

  // Gestion du glisser-déposer PDF
  const handlePDFDrop = async (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const pdfFile = files.find(file => file.type === 'application/pdf');
    
    if (pdfFile) {
      setPdfProcessing(true);
      try {
        console.log('Début extraction PDF:', pdfFile.name);
        
        // Extraction réelle du PDF
        const extractedData = await extractIFCEData(pdfFile);
        const { data: validatedData, warnings } = validateExtractedData(extractedData);
        
        // Afficher les warnings s'il y en a
        if (warnings.length > 0) {
          console.warn('Warnings extraction PDF:', warnings);
          // TODO: Afficher une notification à l'utilisateur
        }
        
        // Pré-remplir le formulaire avec les données extraites
        setFormData(prev => ({ 
          ...prev, 
          ...validatedData,
          // S'assurer que la structure ifceLinks est bien présente
          ifceLinks: validatedData.ifceLinks || {
            main: null,
            pere: null,
            mere: null,
            pereDeMere: null,
            all: {}
          },
          // Préserver les données de surveillance
          surveillanceEtat: prev.surveillanceEtat || 'vert',
          surveillanceMessage: prev.surveillanceMessage || '',
          surveillanceHistorique: prev.surveillanceHistorique || []
        }));
        
        console.log('PDF traité avec succès:', validatedData);
        
      } catch (error) {
        console.error('Erreur extraction PDF:', error);
        alert('Erreur lors de l\'extraction du PDF. Vérifiez que le fichier est bien un certificat IFCE.');
      } finally {
        setPdfProcessing(false);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Navigation vers les tickets
  const handleViewMedicalTickets = () => {
    navigate('/tableau-bord', { state: { filter: 'medical', horseId: horse.id } });
  };

  // Valeurs calculées
  const animalAge = getAge(formData.dateNaissance || (horse && horse.dateNaissance));
  const tagColor = getTagColor(formData.tag || (horse && horse.tag));
  const proprietaireTagColor = getProprietaireTagColor();
  const surveillanceColor = surveillanceColors[formData.surveillanceEtat || (horse && horse.surveillanceEtat)] || surveillanceColors.vert;
  const selectedTagColor = getTagColor(formData.tag);

  if (loading) {
    return (
      <PageWrapper icon={GiHorseHead} title="Chargement...">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            <span className="text-lg text-gray-500">
              {isNew ? 'Préparation du formulaire...' : 'Chargement des informations...'}
            </span>
          </div>
        </div>
      </PageWrapper>
    );
  }

  if (!isNew && !horse) {
    return (
      <PageWrapper icon={GiHorseHead} title="Animal non trouvé">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-lg text-gray-500 mb-4">Animal non trouvé</div>
            <button
              onClick={() => navigate('/animaux')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Retour à la liste
            </button>
          </div>
        </div>
      </PageWrapper>
    );
  }

  const displayData = isNew ? formData : (horse || formData);

  return (
    <PageWrapper 
      icon={GiHorseHead} 
      title={isNew ? 'Nouveau cheval' : (displayData.nom || 'Animal sans nom')}
    >
      {/* Header avec actions */}
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => navigate('/animaux')}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour à la liste
        </button>
        
        <div className="flex gap-3">
          {!isEditing ? (
            <>
              {displayData.sire && (
                <button
                  onClick={() => {
                    const link = getIFCELink('main');
                    if (link) {
                      window.open(link, '_blank');
                    } else {
                      // Fallback : recherche par SIRE (plus fiable que le nom)
                      window.open(getIFCESearchUrl(displayData.nom, displayData.sire), '_blank');
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"
                  title="Voir fiche IFCE"
                >
                  <ExternalLink className="h-4 w-4" />
                  Fiche IFCE
                </button>
              )}
              {!isNew && (
                <button
                  onClick={handleNewActe}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"                >
                  <Syringe className="h-4 w-4" />
                  Nouvel acte
                </button>
              )}
              <button
                onClick={handleEdit}
                className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"
              >
                <Edit className="h-4 w-4" />
                Modifier
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleCancel}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <X className="h-4 w-4" />
                Annuler
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Save className="h-4 w-4" />
                {isNew ? 'Créer' : 'Enregistrer'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Zone de glisser-déposer PDF (visible seulement en mode édition) */}
      {isEditing && (
        <div 
          className="mb-6 border-2 border-dashed border-blue-300 rounded-lg p-8 bg-blue-50/50 hover:bg-blue-50 transition-colors"
          onDrop={handlePDFDrop}
          onDragOver={handleDragOver}
        >
          <div className="text-center">
            {pdfProcessing ? (
              <div className="flex items-center justify-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <div>
                  <p className="text-lg font-medium text-blue-900">Traitement du PDF en cours...</p>
                  <p className="text-sm text-blue-700">Extraction des données IFCE...</p>
                </div>
              </div>
            ) : (
              <div>
                <Upload className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-blue-900 mb-2">
                  Glissez-déposez un PDF IFCE ici
                </h3>
                <p className="text-blue-700">
                  Le formulaire sera automatiquement rempli avec les données du certificat
                </p>
                <p className="text-xs text-blue-600 mt-2">
                  Formats supportés : PDF • Taille max : 10MB
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Carte principale */}
      <div className="bg-white rounded-lg shadow-sm border mb-6">
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div 
                className="p-3 rounded-full"
                style={{ 
                  backgroundColor: tagColor ? `${tagColor.codeHex}20` : '#dbeafe',
                }}
              >
                <GiHorseHead 
                  className="h-8 w-8"
                  style={{ 
                    color: tagColor ? tagColor.codeHex : '#2563eb'
                  }}
                />
              </div>
              <div>
                {isEditing ? (
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleInputChange}
                    className="text-3xl font-bold text-gray-900 mb-2 border-b-2 border-blue-300 bg-transparent focus:outline-none focus:border-blue-500"
                    placeholder="Nom de l'animal"
                    required
                  />
                ) : (
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {displayData.nom || 'Animal sans nom'}
                  </h1>
                )}
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span>Équidé</span>
                  {displayData.race && <span>{displayData.race}</span>}
                  {animalAge !== null && <span>{animalAge} ans</span>}
                  {displayData.sexe && <span>{displayData.sexe}</span>}
                  {displayData.pouliniere && <span className="bg-pink-100 text-pink-800 px-2 py-1 rounded text-xs">Poulinière</span>}
                </div>
              </div>
            </div>
            
            {/* Tag couleur */}
            {isEditing && (
              <div className="w-48">
                <label className="block text-sm font-medium text-gray-700 mb-2">Tag couleur</label>
                <div className="grid grid-cols-3 gap-2">
                  {tagColors.map(tag => (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => handleTagSelect(tag.id)}
                      className={`w-8 h-8 rounded-full border-2 hover:scale-105 transition-all ${
                        selectedTagColor?.id === tag.id 
                          ? 'border-gray-700 ring-2 ring-gray-400' 
                          : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: tag.codeHex }}
                      title={tag.nom}
                    />
                  ))}
                </div>
                {selectedTagColor && (
                  <p className="text-sm text-gray-600 mt-1">{selectedTagColor.nom}</p>
                )}
              </div>
            )}
          </div>

          {/* Section Surveillance et Tickets - Affiché seulement si le cheval existe */}
          {!isNew && (
            <div className="space-y-3 mb-6">
              {/* État de surveillance */}
              <SurveillanceStatus
                currentState={displayData.surveillanceEtat || 'vert'}
                currentMessage={displayData.surveillanceMessage || ''}
                onStateChange={handleSurveillanceChange}
                onShowHistory={() => setShowSurveillanceHistory(true)}
                isEditing={isEditing}
                disabled={false}
              />

              {/* Ligne Tickets médicaux */}
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <FaHandHoldingMedical className="h-5 w-5 text-blue-600" />
                  <div>
                    <span className="text-sm font-medium text-gray-700">Tickets médicaux</span>
                    {medicalTickets.length > 0 && (
                      <div className="text-sm text-blue-600 font-medium mt-1">
                        {medicalTickets.length} ticket{medicalTickets.length > 1 ? 's' : ''} en cours
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowMedicalModal(true)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors text-sm font-medium"
                  >
                    <Plus className="h-4 w-4" />
                    Ouvrir un ticket
                  </button>
                  {medicalTickets.length > 0 && (
                    <button
                      onClick={handleViewMedicalTickets}
                      className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                    >
                      <Eye className="h-4 w-4" />
                      Voir les tickets
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Grille d'informations principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Propriétaire */}
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <User className="h-5 w-5 text-gray-600" />
              <div className="flex-1">
                <div className="text-sm text-gray-600">Propriétaire</div>
                {isEditing ? (
                  <select
                    name="proprietaire"
                    value={formData.proprietaire}
                    onChange={handleInputChange}
                    className="font-medium text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500 w-full"
                    required
                  >
                    <option value="">Choisir…</option>
                    {proprietaires.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.nom} {p.prenom}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="font-medium text-gray-900 flex items-center gap-2">
                    {proprietaireTagColor && (
                      <BsPersonFill 
                        className="w-4 h-4" 
                        style={{ color: proprietaireTagColor }}
                      />
                    )}
                    {proprietaire ? `${proprietaire.nom} ${proprietaire.prenom}` : '-'}
                  </div>
                )}
              </div>
            </div>

            {/* Zone */}
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <MapPin className="h-5 w-5 text-gray-600" />
              <div className="flex-1">
                <div className="text-sm text-gray-600">Localisation</div>
                {isEditing ? (
                  <select
                    name="zoneId"
                    value={formData.zoneId || ""}
                    onChange={handleInputChange}
                    className="font-medium text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500 w-full"
                    required
                  >
                    <option value="">Sélectionner une zone…</option>
                    {zones.map(z => (
                      <option key={z.id} value={z.id}>
                        {z.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="font-medium text-gray-900 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {zone && (
                        <>
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: zone.color }}
                          />
                          {zone.name}
                        </>
                      )}
                      {!zone && '-'}
                    </div>
                    {zone && !isNew && (
                      <button
                        onClick={() => {
                          navigate('/carte', { 
                            state: { 
                              focusAnimalId: displayData.id,
                              animalInfo: {
                                nom: displayData.nom,
                                type: displayData.type,
                                zoneId: displayData.zoneId
                              }
                            } 
                          });
                        }}
                        className="p-2 bg-blue-100 hover:bg-blue-200 rounded-full transition-colors"
                        title="Localiser sur la carte"
                      >
                        <MdWhereToVote className="h-5 w-5 text-blue-600" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Date de naissance */}
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <Calendar className="h-5 w-5 text-gray-600" />
              <div className="flex-1">
                <div className="text-sm text-gray-600">Date de naissance</div>
                {isEditing ? (
                  <input
                    type="date"
                    name="dateNaissance"
                    value={formData.dateNaissance}
                    onChange={handleInputChange}
                    className="font-medium text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500 w-full"
                  />
                ) : (
                  <div className="font-medium text-gray-900">
                    {displayData.dateNaissance ? new Date(displayData.dateNaissance).toLocaleDateString('fr-FR') : '-'}
                  </div>
                )}
                {animalAge !== null && (
                  <div className="text-xs text-gray-500">{animalAge} ans</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section Historique des actes - Nouvelle section après la carte principale */}
      {!isNew && (
        <div className="bg-white rounded-lg shadow-sm border mb-6">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Syringe className="h-5 w-5" />
                Historique des actes
              </h2>
              <button
                onClick={() => setShowActes(!showActes)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                {showActes ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    Masquer
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    Afficher ({actes.length})
                  </>
                )}
              </button>
            </div>

            {showActes && (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {loadingActes ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                ) : actes.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Aucun acte enregistré
                  </div>
                ) : (
                  actes.map(acte => {
                    const intervenant = intervenants.find(i => i.id === acte.intervenantId);
                    
                    return (
<div
  key={acte.id}
  onClick={() => handleActeClick(acte.id)}
  className="p-3 rounded-lg hover:shadow-md cursor-pointer transition-all border-l-4"
  style={{
    backgroundColor: (acte.couleur || '#6b7280') + '10', // Couleur très légère en fond
    borderLeftColor: acte.couleur || '#6b7280' // Bordure gauche avec la couleur
  }}
>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-gray-900">
                                {acte.typeCategorie}
                              </span>
                              {acte.nomActe && (
                                <span className="text-gray-600">
                                  - {acte.nomActe}
                                </span>
                              )}
                              <span className={`
                                inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full
                                ${getStatutColor(acte.statut)}
                              `}>
                                {getStatutIcon(acte.statut)}
                                {getStatutLabel(acte.statut)}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600">
                              <span className="font-medium">
                                {new Date(acte.dateActe).toLocaleDateString('fr-FR')}
                              </span>
                              {acte.heureActe && (
                                <span> à {acte.heureActe}</span>
                              )}
                              {intervenant && (
                                <span> • {intervenant.prenom} {intervenant.nom}</span>
                              )}
                            </div>
                            {acte.description && (
                              <div className="text-sm text-gray-500 mt-1 line-clamp-1">
                                {acte.description}
                              </div>
                            )}
                          </div>
                          {acte.cout && (
                            <div className="text-sm font-medium text-gray-900 ml-4">
                              {acte.cout.toFixed(2)} €
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sections détaillées */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Informations administratives */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Hash className="h-5 w-5" />
              Informations administratives
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600">N° SIRE</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="sire"
                    value={formData.sire}
                    onChange={handleInputChange}
                    className="block w-full font-medium text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
                    placeholder="250259701234567"
                  />
                ) : (
                  <div className="font-medium text-gray-900">{displayData.sire || '-'}</div>
                )}
              </div>
              <div>
                <label className="text-sm text-gray-600">N° Transpondeur</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="transpondeur"
                    value={formData.transpondeur}
                    onChange={handleInputChange}
                    className="block w-full font-medium text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
                    placeholder="250259701234567"
                  />
                ) : (
                  <div className="font-medium text-gray-900">{displayData.transpondeur || '-'}</div>
                )}
              </div>
              <div>
                <label className="text-sm text-gray-600">Race/Stud-book</label>
                {isEditing ? (
                  <select
                    name="race"
                    value={formData.race}
                    onChange={handleInputChange}
                    className="block w-full font-medium text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
                  >
                    <option value="">Choisir…</option>
                    {races.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                ) : (
                  <div className="font-medium text-gray-900">{displayData.race || '-'}</div>
                )}
              </div>
              <div>
                <label className="text-sm text-gray-600">Sexe</label>
                {isEditing ? (
                  <select
                    name="sexe"
                    value={formData.sexe}
                    onChange={handleInputChange}
                    className="block w-full font-medium text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
                  >
                    <option value="">Choisir…</option>
                    <option value="M">M</option>
                    <option value="F">F</option>
                    <option value="Hongre">Hongre</option>
                  </select>
                ) : (
                  <div className="font-medium text-gray-900">{displayData.sexe || '-'}</div>
                )}
              </div>
              <div>
                <label className="text-sm text-gray-600">Catégorie</label>
                {isEditing ? (
                  <select
                    name="categorie"
                    value={formData.categorie}
                    onChange={handleInputChange}
                    className="block w-full font-medium text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
                  >
                    <option value="">Choisir…</option>
                    {categories.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                ) : (
                  <div className="font-medium text-gray-900">{displayData.categorie || '-'}</div>
                )}
              </div>
              <div>
                <label className="text-sm text-gray-600">Couleur</label>
                {isEditing ? (
                  <select
                    name="couleur"
                    value={formData.couleur}
                    onChange={handleInputChange}
                    className="block w-full font-medium text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
                  >
                    <option value="">Sélectionner...</option>
                    {couleurs.map(couleur => (
                      <option key={couleur} value={couleur}>{couleur}</option>
                    ))}
                  </select>
                ) : (
                  <div className="font-medium text-gray-900">{displayData.couleur || '-'}</div>
                )}
              </div>
              {/* Checkbox Poulinière */}
              {isEditing && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="pouliniere"
                    name="pouliniere"
                    checked={formData.pouliniere || false}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                  />
                  <label htmlFor="pouliniere" className="text-sm text-gray-600">
                    Poulinière active
                  </label>
                </div>
              )}
              {/* Lien IFCE principal en mode édition */}
              {isEditing && (
                <div>
                  <label className="text-sm text-gray-600">Lien IFCE principal (optionnel)</label>
                  <input
                    type="url"
                    name="ifceLinks.main"
                    value={formData.ifceLinks?.main || ''}
                    onChange={handleInputChange}
                    className="block w-full font-medium text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
                    placeholder="https://infochevaux.ifce.fr/..."
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Généalogie avec liens stables */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <GiHorseHead className="h-5 w-5" />
              Généalogie
            </h2>
            <div className="space-y-4">
              {/* Père */}
              <div>
                <label className="text-sm text-gray-600">Père</label>
                {isEditing ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      name="pere"
                      value={formData.pere}
                      onChange={handleInputChange}
                      className="block w-full font-medium text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
                      placeholder="Nom du père"
                    />
                    {formData.ifceLinks?.pere && (
                      <input
                        type="url"
                        name="ifceLinks.pere"
                        value={formData.ifceLinks.pere}
                        onChange={handleInputChange}
                        className="block w-full text-xs text-gray-500 bg-transparent border-b border-gray-200 focus:outline-none"
                        placeholder="Lien IFCE du père (optionnel)"
                      />
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-gray-900">{displayData.pere || '-'}</div>
                    {displayData.pere && (() => {
                      const link = getIFCELink('pere');
                      return link ? (
                        <button
                          onClick={() => window.open(link, '_blank')}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title={`Voir fiche IFCE de ${displayData.pere}`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => window.open(getIFCESearchUrl(displayData.pere), '_blank')}
                          className="text-gray-400 hover:text-gray-600 p-1"
                          title={`Rechercher ${displayData.pere} sur IFCE`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      );
                    })()}
                  </div>
                )}
              </div>
              
              {/* Mère */}
              <div>
                <label className="text-sm text-gray-600">Mère</label>
                {isEditing ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      name="mere"
                      value={formData.mere}
                      onChange={handleInputChange}
                      className="block w-full font-medium text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
                      placeholder="Nom de la mère"
                    />
                    {formData.ifceLinks?.mere && (
                      <input
                        type="url"
                        name="ifceLinks.mere"
                        value={formData.ifceLinks.mere}
                        onChange={handleInputChange}
                        className="block w-full text-xs text-gray-500 bg-transparent border-b border-gray-200 focus:outline-none"
                        placeholder="Lien IFCE de la mère (optionnel)"
                      />
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-gray-900">{displayData.mere || '-'}</div>
                    {displayData.mere && (() => {
                      const link = getIFCELink('mere');
                      return link ? (
                        <button
                          onClick={() => window.open(link, '_blank')}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title={`Voir fiche IFCE de ${displayData.mere}`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => window.open(getIFCESearchUrl(displayData.mere), '_blank')}
                          className="text-gray-400 hover:text-gray-600 p-1"
                          title={`Rechercher ${displayData.mere} sur IFCE`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      );
                    })()}
                  </div>
                )}
              </div>
              
              {/* Père de mère */}
              <div>
                <label className="text-sm text-gray-600">Père de mère</label>
                {isEditing ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      name="pereDeMere"
                      value={formData.pereDeMere}
                      onChange={handleInputChange}
                      className="block w-full font-medium text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
                      placeholder="Nom du père de mère"
                    />
                    {formData.ifceLinks?.pereDeMere && (
                      <input
                        type="url"
                        name="ifceLinks.pereDeMere"
                        value={formData.ifceLinks.pereDeMere}
                        onChange={handleInputChange}
                        className="block w-full text-xs text-gray-500 bg-transparent border-b border-gray-200 focus:outline-none"
                        placeholder="Lien IFCE du père de mère (optionnel)"
                      />
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-gray-900">{displayData.pereDeMere || '-'}</div>
                    {displayData.pereDeMere && (() => {
                      const link = getIFCELink('pereDeMere');
                      return link ? (
                        <button
                          onClick={() => window.open(link, '_blank')}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title={`Voir fiche IFCE de ${displayData.pereDeMere}`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => window.open(getIFCESearchUrl(displayData.pereDeMere), '_blank')}
                          className="text-gray-400 hover:text-gray-600 p-1"
                          title={`Rechercher ${displayData.pereDeMere} sur IFCE`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      );
                    })()}
                  </div>
                )}
              </div>
              
              {/* Naisseur */}
              <div>
                <label className="text-sm text-gray-600">Naisseur</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="naisseur"
                    value={formData.naisseur}
                    onChange={handleInputChange}
                    className="block w-full font-medium text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
                    placeholder="Nom du naisseur"
                  />
                ) : (
                  <div className="font-medium text-gray-900">{displayData.naisseur || '-'}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {proprietaire && !isEditing && (
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="h-5 w-5" />
                Contact propriétaire
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-600">Nom complet</label>
                  <div className="font-medium text-gray-900 flex items-center gap-2">
                    {proprietaireTagColor && (
                      <BsPersonFill 
                        className="w-4 h-4" 
                        style={{ color: proprietaireTagColor }}
                      />
                    )}
                    {proprietaire.civilite} {proprietaire.nom} {proprietaire.prenom}
                  </div>
                </div>
                {proprietaire.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-600" />
                    <a 
                      href={`mailto:${proprietaire.email}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {proprietaire.email}
                    </a>
                  </div>
                )}
                {proprietaire.telephone1 && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-600" />
                    <a 
                      href={`tel:${proprietaire.indicatifTel1}${proprietaire.telephone1}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {proprietaire.indicatifTel1} {proprietaire.telephone1}
                    </a>
                  </div>
                )}
                {proprietaire.adresse && (
                  <div>
                    <label className="text-sm text-gray-600">Adresse</label>
                    <div className="font-medium text-gray-900">
                      {proprietaire.adresse}
                      {proprietaire.complementAdresse && (
                        <div>{proprietaire.complementAdresse}</div>
                      )}
                      {(proprietaire.codePostal || proprietaire.ville) && (
                        <div>
                          {proprietaire.codePostal} {proprietaire.ville}
                        </div>
                      )}
                      {proprietaire.pays && (
                        <div>{proprietaire.pays}</div>
                      )}
                    </div>
                  </div>
                )}
                {proprietaire.entreprise && (
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-gray-600" />
                    <div>
                      <label className="text-sm text-gray-600">Entreprise</label>
                      <div className="font-medium text-gray-900">{proprietaire.entreprise}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Notes générales */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Notes générales
            </h2>
            {isEditing ? (
              <textarea
                name="NotesGenerales"
                value={formData.NotesGenerales}
                onChange={handleInputChange}
                rows={4}
                className="w-full text-gray-700 bg-gray-50 border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ajouter des notes générales..."
              />
            ) : (
              <div className="prose prose-sm max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap">{displayData.NotesGenerales || 'Aucune note'}</p>
              </div>
            )}
          </div>
        </div>

        {/* Notes de santé */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Heart className="h-5 w-5" />
              Notes de santé
            </h2>
            {isEditing ? (
              <textarea
                name="NotesSantee"
                value={formData.NotesSantee}
                onChange={handleInputChange}
                rows={4}
                className="w-full text-gray-700 bg-gray-50 border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ajouter des notes de santé..."
              />
            ) : (
              <div className="prose prose-sm max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap">{displayData.NotesSantee || 'Aucune note de santé'}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Modal de ticket médical */}
      <MedicalTicketModal
        isOpen={showMedicalModal}
        onClose={() => setShowMedicalModal(false)}
        horse={displayData}
        onSuccess={() => {
          loadTickets();
        }}
      />
      
      {/* Modal d'historique de surveillance */}
      <SurveillanceHistoryModal
        isOpen={showSurveillanceHistory}
        onClose={() => setShowSurveillanceHistory(false)}
        history={displayData.surveillanceHistorique || []}
      />
    </PageWrapper>
  );
};

export default UnifiedHorsePage;